import React, { FC, ReactElement, Fragment, useEffect, useState } from 'react';
import { Theme, Typography, Box, FormControl, Button } from '@mui/material';
import { createStyles, withStyles, WithStyles } from '@mui/styles';
import { WithTranslation, withTranslation } from 'react-i18next';

import { observer, inject } from 'mobx-react';
import classNames from 'classnames';
import StoreModel from 'models/Index';
import GoMainMenuButton from 'components/common/HomeButton';
import { Services } from 'helpers/constants/Services';
import { TransactionFoundWarningModal } from 'containers/modals';

interface MainPageProps extends WithStyles<typeof styles>, WithTranslation {}
interface MainPageInject extends MainPageProps {
    Store: StoreModel;
}

const styles = (theme: Theme) =>
    createStyles({
        '@global': {
            '.MuiPaper-root': {
                marginTop: 74,
                transform: 'scale(1.2) !important',
            },
            '.MuiPaper-root ul li': {
                fontSize: '1.4rem',
            },
        },
        title: { marginTop: 100 },
        accessCode: {
            display: 'flex',
            width: 420,
            height: 50,
        },
        accessCodeInput: {
            padding: '25px 35px',
            fontSize: '2rem',
        },
        label: {
            fontSize: '1.5rem',
            background: 'linear-gradient(to top, transparent, white 55%)',
        },
        select: {
            fontSize: '1.5rem',
        },
        buttons: {
            display: 'flex',
            fontSize: '2.5rem',
            width: 400,
            padding: '26px 0',
        },
        buttonsForm: {
            justifyContent: 'space-evenly',
            maxHeight: '600px',
            alignItems: 'center',
        },
        red: {
            color: '#FF0101',
            textAlign: 'center',
        },
        statusValue: {
            marginTop: '10px',
            fontSize: '1.6rem',
            fontFamily: 'Montserrat',
        },
    });

const MainPage: FC<MainPageInject> = ({ classes, Store, t }): ReactElement => {
    const { admin } = Store;
    const { registration } = admin.navigationMainPage;
    const [isTransactionFound, setIsTransactionFound] = useState(false);
    const [
        isOpenTransactionFoundWarningModal,
        setIsOpenTransactionFoundWarningModal,
    ] = useState(false);

    useEffect(() => {
        admin
            .login()
            .then(() => admin.retrievePermissions())
            .then(() => Store.collection.getXReport())
            .then((xReport) => {
                if (xReport && xReport.closingPays[0].systemCount) {
                    setIsTransactionFound(true);
                }
            });

        // eslint-disable-next-line
    }, []);

    const onSetupClick = () => {
        if (isTransactionFound) {
            setIsOpenTransactionFoundWarningModal(true);
        } else {
            registration?.onClick();
        }
    };

    return (
        <Fragment>
            <Typography color='primary' variant='h2' align='center'>
                <Box className={classes.title} my={4} py={4}>
                    Main Menu
                </Box>
            </Typography>
            {!admin.hasKioskSetupPermission && (
                <Typography variant='h5' align='center'>
                    <Box mt={4} mb={1} py={4} fontWeight={400}>
                        You do not have permissions for <br />
                        Kiosk Setup.
                    </Box>
                </Typography>
            )}
            {(admin.hasKioskSetupPermission ||
                admin.hasCashCollectionPermission) && (
                <Typography variant='button' align='center'>
                    <Box mt={4} mb={1} py={4} fontWeight={500}>
                        Please select
                    </Box>
                </Typography>
            )}
            <Box display='flex' justifyContent='center' flexGrow={1}>
                <FormControl className={classes.buttonsForm} variant='outlined'>
                    {admin.hasKioskSetupPermission && (
                        <Button
                            variant='primary'
                            className={classes.buttons}
                            onClick={onSetupClick}
                        >
                            <Typography variant='button' align='center'>
                                <Box>Kiosk Setup</Box>
                            </Typography>
                        </Button>
                    )}
                    {!Store.isKioskRegistered && (
                        <div>
                            <Typography
                                variant='h5'
                                className={classNames(
                                    classes.statusValue,
                                    classes.red
                                )}
                            >
                                {t('Kiosk is not registered')}
                            </Typography>
                        </div>
                    )}
                    <div>
                        <Typography
                            variant='h5'
                            className={classNames(
                                classes.statusValue,
                                classes.red
                            )}
                        >
                            {t('CashCollectionMoved')}
                        </Typography>
                    </div>
                    <Box>
                        <GoMainMenuButton />
                    </Box>
                </FormControl>
            </Box>
            <TransactionFoundWarningModal
                isOpen={isOpenTransactionFoundWarningModal}
                onClose={() => {
                    setIsOpenTransactionFoundWarningModal(false);
                }}
            />
        </Fragment>
    );
};

export default withTranslation()(
    withStyles(styles)(inject(Services.STORE)(observer(MainPage)))
);
